import React, {
    useEffect,
    useState,
} from "react";

import Select from "react-select";

import {
    FaPrint,
    FaSearch,
    FaSpinner,
} from "react-icons/fa";

import {
    ToastContainer,
    toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import api from "../../api";


const ExpensePage = () => {

    /* ============================================================
       FORM
    ============================================================ */

    const [form, setForm] = useState({

        category_id: "",

        subcategory_id: "",

        vendor_id: "",

        title: "",

        amount: "",

        expense_date:
            new Date()
                .toISOString()
                .slice(0, 10),

        payment_mode: {
            value: "cash",
            label: "Cash",
        },

        description: "",
    });


    /* ============================================================
       STATE
    ============================================================ */

    const [categories, setCategories] =
        useState([]);

    const [subcategories, setSubcategories] =
        useState([]);

    const [vendors, setVendors] =
        useState([]);

    const [expenses, setExpenses] =
        useState([]);

    const [loading, setLoading] =
        useState(false);

    const [search, setSearch] =
        useState("");


    /* ============================================================
       FETCH CATEGORY
    ============================================================ */

    const fetchCategories = async () => {

        try {

            const res =
                await api.get(
                    "/expense/category/list"
                );


            const list =
                res.data?.data ??
                [];


            setCategories(

                list.map(
                    (c) => ({

                        value:
                            c.id,

                        label:
                            c.name,
                    })
                )
            );

        } catch (error) {

            console.error(error);

            toast.error(
                "Failed to load categories"
            );
        }
    };


    /* ============================================================
       FETCH SUB CATEGORY
    ============================================================ */

    const fetchSubcategories =
        async (
            category_id
        ) => {

            if (
                !category_id
            ) {

                setSubcategories([]);

                return;
            }


            try {

                const res =
                    await api.get(

                        `/expense/subcategory/list?category_id=${category_id}`
                    );


                const list =
                    res.data?.data ??
                    [];


                setSubcategories(

                    list.map(
                        (s) => ({

                            value:
                                s.id,

                            label:
                                s.name,
                        })
                    )
                );

            } catch (error) {

                console.error(error);

                toast.error(
                    "Failed to load subcategories"
                );
            }
        };


    /* ============================================================
       FETCH VENDORS
    ============================================================ */

    const fetchVendors = async () => {

        try {

            const res =
                await api.get(
                    "/vendor/list"
                );


            const list =
                res.data?.data ??
                [];


            setVendors(

                list.map(
                    (v) => ({

                        value:
                            v.id,

                        label:
                            v.name,
                    })
                )
            );

        } catch (error) {

            console.error(error);

            toast.error(
                "Failed to load vendors"
            );
        }
    };


    /* ============================================================
       FETCH EXPENSES
    ============================================================ */

    const fetchExpenses = async () => {

        try {

            const res =
                await api.get(
                    "/expenses/list?limit=100"
                );


            setExpenses(

                res.data?.data ??
                []
            );

        } catch (error) {

            console.error(error);

            toast.error(
                "Failed to load expenses"
            );
        }
    };


    /* ============================================================
       LOAD
    ============================================================ */

    useEffect(() => {

        fetchCategories();

        fetchVendors();

        fetchExpenses();

    }, []);


    useEffect(() => {

        fetchSubcategories(
            form.category_id
        );

    }, [
        form.category_id,
    ]);


    /* ============================================================
       SUBMIT
    ============================================================ */

    const handleSubmit =
        async (e) => {

            e.preventDefault();


            if (
                !form.category_id
            ) {

                return toast.error(
                    "Category required"
                );
            }


            if (
                !form.subcategory_id
            ) {

                return toast.error(
                    "Sub Category required"
                );
            }


            if (
                !form.title.trim()
            ) {

                return toast.error(
                    "Title required"
                );
            }


            if (
                !form.amount ||
                Number(
                    form.amount
                ) <= 0
            ) {

                return toast.error(
                    "Valid amount required"
                );
            }


            const payload = {

                category_id:
                    Number(
                        form.category_id
                    ),

                subcategory_id:
                    form.subcategory_id ||
                    null,

                vendor_id:
                    form.vendor_id ||
                    null,

                title:
                    form.title,

                amount:
                    Number(
                        form.amount
                    ),

                expense_date:
                    form.expense_date,

                payment_mode:
                    form.payment_mode
                        ?.value,

                description:
                    form.description ||
                    null,
            };


            try {

                setLoading(true);


                const res =
                    await api.post(
                        "/expenses/add",
                        payload
                    );


                if (
                    res.data?.status
                ) {

                    toast.success(
                        "Expense added successfully"
                    );


                    await fetchExpenses();


                    setForm({

                        category_id:
                            "",

                        subcategory_id:
                            "",

                        vendor_id:
                            "",

                        title:
                            "",

                        amount:
                            "",

                        expense_date:
                            new Date()
                                .toISOString()
                                .slice(
                                    0,
                                    10
                                ),

                        payment_mode: {
                            value:
                                "cash",

                            label:
                                "Cash",
                        },

                        description:
                            "",
                    });

                } else {

                    toast.error(

                        res.data?.message ||
                        "Failed to save expense"
                    );
                }

            } catch (error) {

                console.error(error);

                toast.error(
                    "Network error"
                );

            } finally {

                setLoading(false);
            }
        };


    /* ============================================================
       HELPERS
    ============================================================ */

    const money = (
        amount
    ) => {

        return Number(
            amount ||
            0
        ).toLocaleString(
            "en-IN"
        );
    };


    const formatDate = (
        value
    ) => {

        if (!value) {
            return "-";
        }


        const onlyDate =
            String(value)
                .split(" ")[0];


        const parts =
            onlyDate
                .split("-");


        if (
            parts.length === 3
        ) {

            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }


        return value;
    };


    const escapeHtml = (
        value
    ) => {

        return String(
            value ??
            ""
        )

            .replace(
                /&/g,
                "&amp;"
            )

            .replace(
                /</g,
                "&lt;"
            )

            .replace(
                />/g,
                "&gt;"
            )

            .replace(
                /"/g,
                "&quot;"
            )

            .replace(
                /'/g,
                "&#039;"
            );
    };


    /* ============================================================
       SCHOOL INFORMATION
       SAME PATTERN AS STUDENT BILL
    ============================================================ */

    const getPrintSchoolInfo =
        () => {

            const LOGO_BASE =

                "https://dgps.co.in/public/admin/assets/img/schools/logo";


            const defaultLogo =

                "https://dgps.co.in/public/admin/assets/img/logo.png";


            const resolveLogo =
                (logo) => {

                    if (!logo) {

                        return defaultLogo;
                    }


                    if (
                        /^https?:\/\//i
                            .test(
                                logo
                            ) ||

                        String(
                            logo
                        )
                            .startsWith(
                                "data:"
                            )
                    ) {

                        return logo;
                    }


                    return (

                        `${LOGO_BASE}/${String(
                            logo
                        ).replace(
                            /^\/+/,
                            ""
                        )}`
                    );
                };


            try {

                const rawSchools =

                    localStorage.getItem(
                        "schools"
                    );


                const schoolId =

                    localStorage.getItem(
                        "school_id"
                    );


                if (
                    rawSchools
                ) {

                    const parsed =
                        JSON.parse(
                            rawSchools
                        );


                    const chosen =

                        parsed.find(
                            (school) =>

                                String(
                                    school.id
                                ) ===
                                String(
                                    schoolId
                                )
                        )

                        ||

                        parsed[0];


                    if (
                        chosen
                    ) {

                        return {

                            name:

                                (
                                    chosen.name ||
                                    "Dormant Garden Public School"
                                )
                                    .toUpperCase(),

                            address:

                                chosen.address ||
                                "",

                            phone:

                                chosen.phone ||
                                "",

                            email:

                                chosen.email ||
                                "",

                            logo:

                                resolveLogo(
                                    chosen.logo
                                ),

                            defaultLogo:
                                defaultLogo,
                        };
                    }
                }


                return {

                    name:

                        (
                            localStorage.getItem(
                                "school_name"
                            )

                            ||

                            "Dormant Garden Public School"
                        )
                            .toUpperCase(),

                    address:

                        localStorage.getItem(
                            "school_address"
                        )

                        ||

                        "",

                    phone:

                        localStorage.getItem(
                            "school_phone"
                        )

                        ||

                        "",

                    email:

                        localStorage.getItem(
                            "school_email"
                        )

                        ||

                        "",

                    logo:

                        resolveLogo(

                            localStorage.getItem(
                                "school_logo"
                            )

                            ||

                            localStorage.getItem(
                                "school_logo_bk"
                            )
                        ),

                    defaultLogo:
                        defaultLogo,
                };


            } catch (
                error
            ) {

                console.error(
                    error
                );


                return {

                    name:
                        "DORMANT GARDEN PUBLIC SCHOOL",

                    address:
                        "",

                    phone:
                        "",

                    email:
                        "",

                    logo:
                        defaultLogo,

                    defaultLogo:
                        defaultLogo,
                };
            }
        };


    /* ============================================================
       EXPENSE VALUE HELPERS
    ============================================================ */

    const getCategory =
        (item) => {

            return (

                item.category_name ||

                item.category ||

                "-"
            );
        };


    const getSubCategory =
        (item) => {

            return (

                item.subcategory_name ||

                item.subcategory ||

                "-"
            );
        };


    const getVendor =
        (item) => {

            return (

                item.vendor_name ||

                item.vendor ||

                "-"
            );
        };


    /* ============================================================
       PRINT EXPENSE RECEIPT
       DUAL A4 LIKE STUDENT BILL
    ============================================================ */

    const printExpenseReceipt =
        (item) => {

            try {

                const printWindow =
                    window.open(
                        "",
                        "_blank",
                        "width=1100,height=900"
                    );


                if (
                    !printWindow
                ) {

                    toast.error(
                        "Popup blocked. Please allow popup."
                    );

                    return;
                }


                const school =
                    getPrintSchoolInfo();


                const voucherNo =

                    `EXP-${String(
                        item.id ||
                        0
                    ).padStart(
                        5,
                        "0"
                    )}`;


                const category =
                    getCategory(
                        item
                    );


                const subCategory =
                    getSubCategory(
                        item
                    );


                const vendor =
                    getVendor(
                        item
                    );


                const paymentMode =

                    String(

                        item.payment_mode ||

                        "-"
                    )
                        .toUpperCase();


                const date =

                    formatDate(
                        item.expense_date
                    );


                const amount =

                    money(
                        item.amount
                    );


                const description =

                    item.description ||

                    "-";


                /* ====================================================
                   ONE RECEIPT
                ==================================================== */

                const createReceipt =
                    (
                        copyType
                    ) => {

                        return `

                            <div class="receipt-box">


                                <!-- SCHOOL HEADER -->

                                <div class="school-header">


                                    <div class="logo-column">

                                        <img

                                            src="${escapeHtml(
                                                school.logo
                                            )}"

                                            onerror="
                                                this.onerror=null;
                                                this.src='${escapeHtml(
                                                    school.defaultLogo
                                                )}';
                                            "

                                            alt="School Logo"

                                            class="school-logo"
                                        />

                                    </div>


                                    <div class="school-text">


                                        <div class="school-name">

                                            ${escapeHtml(
                                                school.name
                                            )}

                                        </div>


                                        <div class="school-contact">

                                            ${
                                                school.address

                                                    ? `${escapeHtml(
                                                        school.address
                                                    )}<br/>`

                                                    : ""
                                            }


                                            ${
                                                school.phone

                                                    ? `Phone: ${escapeHtml(
                                                        school.phone
                                                    )}`

                                                    : ""
                                            }


                                            ${
                                                school.phone &&
                                                school.email

                                                    ? " | "

                                                    : ""
                                            }


                                            ${
                                                school.email

                                                    ? `Email: ${escapeHtml(
                                                        school.email
                                                    )}`

                                                    : ""
                                            }

                                        </div>


                                    </div>


                                    <div class="logo-space">

                                    </div>


                                </div>


                                <!-- BLUE TITLE -->

                                <div class="receipt-header">

                                    Expense Receipt

                                </div>


                                <!-- COPY -->

                                <div class="copy-label">

                                    ${escapeHtml(
                                        copyType
                                    )}

                                </div>


                                <!-- EXPENSE DETAILS TITLE -->

                                <div class="section-title">

                                    Expense Details

                                </div>


                                <!-- MAIN INFO -->

                                <div class="info-row">


                                    <p>

                                        <strong>
                                            Voucher No:
                                        </strong>

                                        ${escapeHtml(
                                            voucherNo
                                        )}

                                    </p>


                                    <p>

                                        <strong>
                                            Expense Date:
                                        </strong>

                                        ${escapeHtml(
                                            date
                                        )}

                                    </p>


                                    <p>

                                        <strong>
                                            Payment Mode:
                                        </strong>

                                        ${escapeHtml(
                                            paymentMode
                                        )}

                                    </p>


                                </div>


                                <!-- TABLE TITLE -->

                                <div class="section-title">

                                    Expense Information

                                </div>


                                <table>


                                    <thead>

                                        <tr>

                                            <th>
                                                Category
                                            </th>

                                            <th>
                                                Sub Category
                                            </th>

                                            <th>
                                                Title
                                            </th>

                                            <th>
                                                Amount
                                            </th>

                                        </tr>

                                    </thead>


                                    <tbody>

                                        <tr>

                                            <td>

                                                ${escapeHtml(
                                                    category
                                                )}

                                            </td>

                                            <td>

                                                ${escapeHtml(
                                                    subCategory
                                                )}

                                            </td>

                                            <td>

                                                ${escapeHtml(
                                                    item.title ||
                                                    "-"
                                                )}

                                            </td>

                                            <td class="amount">

                                                ₹ ${escapeHtml(
                                                    amount
                                                )}

                                            </td>

                                        </tr>

                                    </tbody>


                                </table>


                                <!-- EXTRA DETAILS -->

                                <div class="bottom-info">


                                    <div>

                                        <strong>
                                            Vendor:
                                        </strong>

                                        ${escapeHtml(
                                            vendor
                                        )}

                                    </div>


                                    <div>

                                        <strong>
                                            Description:
                                        </strong>

                                        ${escapeHtml(
                                            description
                                        )}

                                    </div>


                                </div>


                                <!-- TOTAL -->

                                <div class="total-row">


                                    <span>

                                        Total Expense:

                                    </span>


                                    <strong>

                                        ₹ ${escapeHtml(
                                            amount
                                        )}

                                    </strong>


                                </div>


                                <!-- FOOTER -->

                                <div class="footer-row">


                                    <div>

                                        <strong>
                                            Prepared By:
                                        </strong>

                                        ${
                                            escapeHtml(

                                                localStorage.getItem(
                                                    "name"
                                                )

                                                ||

                                                localStorage.getItem(
                                                    "user_name"
                                                )

                                                ||

                                                "-"
                                            )
                                        }

                                    </div>


                                    <div>

                                        <strong>
                                            Voucher:
                                        </strong>

                                        ${escapeHtml(
                                            voucherNo
                                        )}

                                    </div>


                                </div>


                                <div class="receipt-footer">

                                    This is a system-generated expense receipt and doesn't require signature.

                                </div>


                            </div>
                        `;
                    };


                /* ====================================================
                   PRINT HTML
                ==================================================== */

                printWindow.document.write(`

                    <!DOCTYPE html>

                    <html>


                    <head>


                        <title>
                            Expense Receipt
                        </title>


                        <meta
                            charset="UTF-8"
                        />


                        <style>


                            /* =========================================
                               A4 PAGE
                            ========================================= */

                            @page {

                                size:
                                    A4 portrait;

                                margin:
                                    7mm;
                            }


                            * {

                                box-sizing:
                                    border-box;
                            }


                            html,
                            body {

                                margin:
                                    0;

                                padding:
                                    0;

                                width:
                                    100%;

                                background:
                                    #ffffff;

                                font-family:
                                    Arial,
                                    "Segoe UI",
                                    sans-serif;

                                color:
                                    #000000;

                                -webkit-print-color-adjust:
                                    exact !important;

                                print-color-adjust:
                                    exact !important;
                            }


                            body {

                                padding:
                                    0;
                            }


                            .receipt-container {

                                width:
                                    100%;

                                margin:
                                    0 auto;
                            }


                            /* =========================================
                               RECEIPT BOX
                            ========================================= */

                            .receipt-box {

                                width:
                                    100%;

                                min-height:
                                    117mm;

                                max-height:
                                    124mm;

                                overflow:
                                    hidden;

                                background:
                                    #ffffff;

                                border:
                                    1.5px dashed #b8b8b8;

                                border-radius:
                                    4px;

                                padding:
                                    4mm;

                                margin:
                                    0 0 8mm 0;

                                page-break-inside:
                                    avoid;

                                break-inside:
                                    avoid;
                            }


                            .receipt-box:last-child {

                                margin-bottom:
                                    0;
                            }


                            /* =========================================
                               SCHOOL HEADER
                            ========================================= */

                            .school-header {

                                width:
                                    100%;

                                min-height:
                                    18mm;

                                display:
                                    grid;

                                grid-template-columns:
                                    22mm 1fr 22mm;

                                align-items:
                                    center;
                            }


                            .logo-column {

                                display:
                                    flex;

                                align-items:
                                    center;

                                justify-content:
                                    flex-start;
                            }


                            .school-logo {

                                width:
                                    18mm;

                                height:
                                    18mm;

                                object-fit:
                                    contain;
                            }


                            .school-text {

                                text-align:
                                    center;
                            }


                            .school-name {

                                color:
                                    #003b7a;

                                font-size:
                                    16px;

                                font-weight:
                                    900;

                                line-height:
                                    1.15;
                            }


                            .school-contact {

                                margin-top:
                                    1mm;

                                color:
                                    #333333;

                                font-size:
                                    8px;

                                line-height:
                                    1.35;
                            }


                            /* =========================================
                               RECEIPT HEADER
                            ========================================= */

                            .receipt-header {

                                margin-top:
                                    2mm;

                                padding:
                                    2.2mm;

                                background:
                                    #003b7a;

                                color:
                                    #ffffff;

                                text-align:
                                    center;

                                font-size:
                                    13px;

                                font-weight:
                                    900;
                            }


                            .copy-label {

                                text-align:
                                    center;

                                color:
                                    #929292;

                                font-size:
                                    8px;

                                padding:
                                    2mm 0;
                            }


                            /* =========================================
                               SECTION TITLE
                            ========================================= */

                            .section-title {

                                margin:
                                    1mm 0 1.5mm;

                                padding-bottom:
                                    1.2mm;

                                text-align:
                                    center;

                                color:
                                    #003b7a;

                                font-size:
                                    10px;

                                font-weight:
                                    900;

                                border-bottom:
                                    1px solid #cfcfcf;
                            }


                            /* =========================================
                               INFO
                            ========================================= */

                            .info-row {

                                width:
                                    100%;

                                display:
                                    flex;

                                justify-content:
                                    space-between;

                                align-items:
                                    center;

                                gap:
                                    4mm;

                                padding:
                                    1mm 0;
                            }


                            .info-row p {

                                margin:
                                    0;

                                font-size:
                                    8.5px;

                                white-space:
                                    nowrap;
                            }


                            /* =========================================
                               TABLE
                            ========================================= */

                            table {

                                width:
                                    100%;

                                border-collapse:
                                    collapse;

                                table-layout:
                                    fixed;
                            }


                            th,
                            td {

                                border:
                                    1px solid #bfc5cc;

                                padding:
                                    2mm;

                                font-size:
                                    8.5px;

                                text-align:
                                    left;

                                vertical-align:
                                    middle;

                                word-break:
                                    break-word;
                            }


                            th {

                                background:
                                    #dceafa;

                                color:
                                    #000000;

                                font-weight:
                                    900;
                            }


                            td.amount {

                                font-weight:
                                    900;

                                white-space:
                                    nowrap;
                            }


                            /* =========================================
                               BOTTOM INFO
                            ========================================= */

                            .bottom-info {

                                display:
                                    grid;

                                grid-template-columns:
                                    1fr 1.8fr;

                                gap:
                                    3mm;

                                margin-top:
                                    2mm;

                                font-size:
                                    8px;
                            }


                            .bottom-info > div {

                                min-height:
                                    8mm;

                                padding:
                                    2mm;

                                background:
                                    #fafafa;

                                border:
                                    1px solid #e0e0e0;
                            }


                            /* =========================================
                               TOTAL
                            ========================================= */

                            .total-row {

                                margin-top:
                                    2mm;

                                display:
                                    flex;

                                align-items:
                                    center;

                                justify-content:
                                    flex-end;

                                gap:
                                    3mm;

                                font-size:
                                    9px;
                            }


                            .total-row strong {

                                color:
                                    #087a3c;

                                font-size:
                                    12px;
                            }


                            /* =========================================
                               FOOTER
                            ========================================= */

                            .footer-row {

                                margin-top:
                                    2mm;

                                display:
                                    flex;

                                align-items:
                                    center;

                                justify-content:
                                    space-between;

                                gap:
                                    5mm;

                                font-size:
                                    7.5px;
                            }


                            .receipt-footer {

                                margin-top:
                                    3mm;

                                color:
                                    #999999;

                                text-align:
                                    center;

                                font-size:
                                    7px;
                            }


                            /* =========================================
                               PRINT
                            ========================================= */

                            @media print {


                                body {

                                    background:
                                        #ffffff;

                                    padding:
                                        0;
                                }


                                .receipt-box {

                                    box-shadow:
                                        none;
                                }

                            }


                        </style>


                    </head>


                    <body>


                        <div class="receipt-container">


                            ${createReceipt(
                                "Office Copy"
                            )}


                            ${createReceipt(
                                "Accounts Copy"
                            )}


                        </div>


                        <script>


                            function startPrint() {


                                var images =
                                    document.images;


                                var total =
                                    images.length;


                                if (
                                    total === 0
                                ) {

                                    window.print();

                                    return;
                                }


                                var loaded =
                                    0;


                                function done() {

                                    loaded++;


                                    if (
                                        loaded >=
                                        total
                                    ) {

                                        setTimeout(
                                            function () {

                                                window.print();

                                            },
                                            250
                                        );
                                    }
                                }


                                for (
                                    var i = 0;
                                    i < total;
                                    i++
                                ) {


                                    if (
                                        images[i]
                                            .complete
                                    ) {

                                        done();

                                    } else {

                                        images[i]
                                            .onload =
                                            done;


                                        images[i]
                                            .onerror =
                                            done;
                                    }
                                }
                            }


                            window.onload =
                                startPrint;


                            window.onafterprint =
                                function () {

                                    window.close();
                                };


                        </script>


                    </body>


                    </html>
                `);


                printWindow.document
                    .close();


            } catch (
                error
            ) {

                console.error(
                    "Expense print error:",
                    error
                );


                toast.error(
                    "Unable to print expense receipt"
                );
            }
        };


    /* ============================================================
       SEARCH
    ============================================================ */

    const filteredExpenses =

        expenses.filter(
            (item) => {

                const keyword =

                    search
                        .trim()
                        .toLowerCase();


                if (
                    !keyword
                ) {

                    return true;
                }


                return [

                    item.title,

                    item.category,

                    item.category_name,

                    item.subcategory,

                    item.subcategory_name,

                    item.vendor,

                    item.vendor_name,

                    item.expense_date,

                    item.amount,

                ].some(
                    (value) =>

                        String(
                            value ||
                            ""
                        )
                            .toLowerCase()
                            .includes(
                                keyword
                            )
                );
            }
        );


    /* ============================================================
       TOTAL
    ============================================================ */

    const totalSpent =

        expenses.reduce(
            (
                total,
                item
            ) => {

                return (

                    total +

                    Number(
                        item.amount ||
                        0
                    )
                );

            },
            0
        );


    /* ============================================================
       UI
    ============================================================ */

    return (

        <div style={page}>


            <ToastContainer
                position="top-right"
                theme="colored"
            />


            {/* ====================================================
                HEADER
            ==================================================== */}

            <div style={header}>


                <div>


                    <div style={headerTitle}>

                        Expense Dashboard

                    </div>


                    <div style={headerSub}>

                        Manage and track your
                        financial activity

                    </div>


                </div>


                <div style={summaryCard}>


                    ₹{money(
                        totalSpent
                    )}


                    <span style={summaryLabel}>

                        Total Spent

                    </span>


                </div>


            </div>


            {/* ====================================================
                MAIN GRID
            ==================================================== */}

            <div style={mainGrid}>


                {/* =================================================
                    LEFT FORM
                ================================================= */}

                <div style={glassCard}>


                    <div style={formTitle}>

                        Add Expense

                    </div>


                    <form
                        onSubmit={
                            handleSubmit
                        }
                    >


                        <div style={formGrid}>


                            {/* CATEGORY */}

                            <Select

                                placeholder="Category"

                                options={
                                    categories
                                }

                                isSearchable

                                isClearable

                                value={
                                    categories.find(
                                        (x) =>
                                            x.value ===
                                            form.category_id
                                    ) ||
                                    null
                                }

                                onChange={
                                    (opt) =>

                                        setForm(
                                            (state) => ({

                                                ...state,

                                                category_id:
                                                    opt?.value ||
                                                    "",

                                                subcategory_id:
                                                    "",
                                            })
                                        )
                                }

                                styles={
                                    premiumSelect
                                }
                            />


                            {/* SUB CATEGORY */}

                            <Select

                                placeholder="Sub Category"

                                options={
                                    subcategories
                                }

                                isSearchable

                                isClearable

                                value={
                                    subcategories.find(
                                        (x) =>
                                            x.value ===
                                            form.subcategory_id
                                    ) ||
                                    null
                                }

                                onChange={
                                    (opt) =>

                                        setForm(
                                            (state) => ({

                                                ...state,

                                                subcategory_id:
                                                    opt?.value ||
                                                    "",
                                            })
                                        )
                                }

                                styles={
                                    premiumSelect
                                }
                            />


                            {/* VENDOR */}

                            <Select

                                placeholder="Vendor (Optional)"

                                options={
                                    vendors
                                }

                                isSearchable

                                isClearable

                                value={
                                    vendors.find(
                                        (x) =>
                                            x.value ===
                                            form.vendor_id
                                    ) ||
                                    null
                                }

                                onChange={
                                    (opt) =>

                                        setForm(
                                            (state) => ({

                                                ...state,

                                                vendor_id:
                                                    opt?.value ||
                                                    "",
                                            })
                                        )
                                }

                                styles={
                                    premiumSelect
                                }
                            />


                            {/* TITLE */}

                            <input

                                type="text"

                                placeholder="Title"

                                value={
                                    form.title
                                }

                                onChange={
                                    (e) =>

                                        setForm(
                                            (state) => ({

                                                ...state,

                                                title:
                                                    e.target
                                                        .value,
                                            })
                                        )
                                }

                                style={
                                    premiumInput
                                }
                            />


                            {/* AMOUNT */}

                            <input

                                type="number"

                                placeholder="Amount"

                                value={
                                    form.amount
                                }

                                onChange={
                                    (e) =>

                                        setForm(
                                            (state) => ({

                                                ...state,

                                                amount:
                                                    e.target
                                                        .value,
                                            })
                                        )
                                }

                                style={
                                    premiumInput
                                }
                            />


                            {/* DATE */}

                            <input

                                type="date"

                                value={
                                    form.expense_date
                                }

                                onChange={
                                    (e) =>

                                        setForm(
                                            (state) => ({

                                                ...state,

                                                expense_date:
                                                    e.target
                                                        .value,
                                            })
                                        )
                                }

                                style={
                                    premiumInput
                                }
                            />


                            {/* DESCRIPTION */}

                            <input

                                type="text"

                                placeholder="Description"

                                value={
                                    form.description
                                }

                                onChange={
                                    (e) =>

                                        setForm(
                                            (state) => ({

                                                ...state,

                                                description:
                                                    e.target
                                                        .value,
                                            })
                                        )
                                }

                                style={
                                    premiumInput
                                }
                            />


                            {/* PAYMENT MODE */}

                            <Select

                                placeholder="Payment"

                                options={[
                                    {
                                        value:
                                            "cash",

                                        label:
                                            "Cash",
                                    },
                                    {
                                        value:
                                            "upi",

                                        label:
                                            "UPI",
                                    },
                                    {
                                        value:
                                            "bank",

                                        label:
                                            "Bank",
                                    },
                                    {
                                        value:
                                            "card",

                                        label:
                                            "Card",
                                    },
                                ]}

                                isSearchable

                                isClearable

                                value={
                                    form.payment_mode
                                }

                                onChange={
                                    (opt) =>

                                        setForm(
                                            (state) => ({

                                                ...state,

                                                payment_mode:
                                                    opt,
                                            })
                                        )
                                }

                                styles={
                                    premiumSelect
                                }
                            />


                        </div>


                        <button

                            type="submit"

                            style={
                                premiumButton
                            }

                            disabled={
                                loading
                            }
                        >

                            {loading ? (

                                <FaSpinner className="spin" />

                            ) : (

                                "Save Expense"
                            )}

                        </button>


                    </form>


                </div>


                {/* =================================================
                    RIGHT LIST
                ================================================= */}

                <div style={listContainer}>


                    {/* SEARCH */}

                    <div style={stickySearch}>


                        <FaSearch
                            style={{
                                color:
                                    "#94a3b8",
                            }}
                        />


                        <input

                            placeholder="Search expenses..."

                            value={
                                search
                            }

                            onChange={
                                (e) =>
                                    setSearch(
                                        e.target
                                            .value
                                    )
                            }

                            style={
                                searchInput
                            }
                        />


                    </div>


                    {/* EXPENSE LIST */}

                    <div style={expenseList}>


                        {filteredExpenses.map(
                            (item) => (

                                <div
                                    key={
                                        item.id
                                    }

                                    style={
                                        expenseCard
                                    }

                                    onMouseEnter={
                                        (e) =>

                                            e.currentTarget
                                                .style
                                                .transform =
                                                "translateY(-3px)"
                                    }

                                    onMouseLeave={
                                        (e) =>

                                            e.currentTarget
                                                .style
                                                .transform =
                                                "translateY(0px)"
                                    }
                                >


                                    {/* LEFT */}

                                    <div style={expenseLeft}>


                                        <div style={expenseTitle}>

                                            {
                                                item.title
                                            }

                                        </div>


                                        <div style={expenseMeta}>

                                            {formatDate(
                                                item.expense_date
                                            )}

                                            {" • "}

                                            {getCategory(
                                                item
                                            )}

                                        </div>


                                    </div>


                                    {/* RIGHT */}

                                    <div style={expenseRight}>


                                        <div style={amountStyle}>

                                            ₹{money(
                                                item.amount
                                            )}

                                        </div>


                                        <button

                                            type="button"

                                            style={
                                                printButton
                                            }

                                            onClick={() =>
                                                printExpenseReceipt(
                                                    item
                                                )
                                            }
                                        >

                                            <FaPrint />

                                            Print / PDF

                                        </button>


                                    </div>


                                </div>

                            )
                        )}


                        {!filteredExpenses.length && (

                            <div style={emptyBox}>

                                No expenses found.

                            </div>

                        )}


                    </div>


                </div>


            </div>


        </div>
    );
};


/* ============================================================
   STYLES
============================================================ */

const page = {

    minHeight:
        "100vh",

    padding:
        "10px",

    fontFamily:
        "Inter, sans-serif",

    background:
        "#f1f5f9",

    color:
        "#0f172a",
};


const header = {

    display:
        "flex",

    justifyContent:
        "space-between",

    alignItems:
        "center",

    marginBottom:
        "40px",

    flexWrap:
        "wrap",

    gap:
        "20px",
};


const headerTitle = {

    fontSize:
        "28px",

    fontWeight:
        700,

    color:
        "#0f172a",
};


const headerSub = {

    fontSize:
        "20px",

    color:
        "#64748b",

    marginTop:
        "4px",
};


const summaryCard = {

    background:
        "linear-gradient(135deg,#6366f1,#8b5cf6)",

    padding:
        "20px 28px",

    borderRadius:
        "16px",

    fontSize:
        "22px",

    fontWeight:
        700,

    display:
        "flex",

    flexDirection:
        "column",

    alignItems:
        "center",

    color:
        "white",

    boxShadow:
        "0 10px 25px rgba(99,102,241,0.25)",
};


const summaryLabel = {

    fontSize:
        "12px",

    fontWeight:
        400,

    marginTop:
        "6px",

    opacity:
        0.8,
};


const mainGrid = {

    display:
        "grid",

    gridTemplateColumns:
        "420px minmax(0,1fr)",

    gap:
        "40px",
};


const glassCard = {

    background:
        "white",

    borderRadius:
        "20px",

    padding:
        "30px",

    border:
        "1px solid #e2e8f0",

    boxShadow:
        "0 8px 30px rgba(0,0,0,0.05)",
};


const formTitle = {

    fontSize:
        "18px",

    fontWeight:
        600,

    marginBottom:
        "20px",

    color:
        "#0f172a",
};


const formGrid = {

    display:
        "grid",

    gap:
        "16px",

    marginBottom:
        "20px",
};


const premiumInput = {

    height:
        "44px",

    borderRadius:
        "12px",

    border:
        "1px solid #e2e8f0",

    background:
        "#f8fafc",

    padding:
        "0 14px",

    color:
        "#0f172a",

    outline:
        "none",
};


const premiumButton = {

    width:
        "100%",

    height:
        "44px",

    borderRadius:
        "14px",

    border:
        "none",

    fontWeight:
        600,

    background:
        "linear-gradient(135deg,#6366f1,#8b5cf6)",

    color:
        "white",

    cursor:
        "pointer",

    boxShadow:
        "0 8px 20px rgba(99,102,241,0.25)",
};


const premiumSelect = {

    control:
        (base) => ({

            ...base,

            background:
                "#f8fafc",

            border:
                "1px solid #e2e8f0",

            borderRadius:
                "12px",

            minHeight:
                "44px",

            boxShadow:
                "none",
        }),


    singleValue:
        (base) => ({

            ...base,

            color:
                "#0f172a",
        }),


    menu:
        (base) => ({

            ...base,

            background:
                "white",

            borderRadius:
                "12px",

            zIndex:
                9999,
        }),
};


const listContainer = {

    minWidth:
        0,

    display:
        "flex",

    flexDirection:
        "column",
};


const stickySearch = {

    position:
        "sticky",

    top:
        0,

    zIndex:
        5,

    display:
        "flex",

    gap:
        "10px",

    background:
        "white",

    padding:
        "14px 16px",

    borderRadius:
        "14px",

    marginBottom:
        "20px",

    alignItems:
        "center",

    border:
        "1px solid #e2e8f0",

    boxShadow:
        "0 4px 12px rgba(0,0,0,0.04)",
};


const searchInput = {

    flex:
        1,

    border:
        "none",

    outline:
        "none",

    background:
        "transparent",

    color:
        "#0f172a",

    fontSize:
        "14px",
};


const expenseList = {

    display:
        "flex",

    flexDirection:
        "column",

    gap:
        "16px",

    maxHeight:
        "650px",

    overflowY:
        "auto",
};


const expenseCard = {

    background:
        "white",

    padding:
        "18px",

    borderRadius:
        "16px",

    display:
        "flex",

    justifyContent:
        "space-between",

    alignItems:
        "center",

    gap:
        "20px",

    transition:
        "0.2s ease",

    border:
        "1px solid #e2e8f0",

    boxShadow:
        "0 4px 15px rgba(0,0,0,0.04)",
};


const expenseLeft = {

    minWidth:
        0,

    flex:
        1,
};


const expenseRight = {

    display:
        "flex",

    alignItems:
        "center",

    gap:
        "18px",

    flex:
        "0 0 auto",
};


const expenseTitle = {

    fontSize:
        "16px",

    fontWeight:
        700,

    color:
        "#0f172a",
};


const expenseMeta = {

    fontSize:
        "12px",

    color:
        "#64748b",

    marginTop:
        "4px",
};


const amountStyle = {

    fontSize:
        "18px",

    fontWeight:
        800,

    color:
        "#6366f1",

    whiteSpace:
        "nowrap",
};


const printButton = {

    minHeight:
        "36px",

    padding:
        "0 13px",

    border:
        "none",

    borderRadius:
        "9px",

    display:
        "inline-flex",

    alignItems:
        "center",

    justifyContent:
        "center",

    gap:
        "7px",

    background:
        "#003b7a",

    color:
        "#ffffff",

    fontSize:
        "11px",

    fontWeight:
        750,

    cursor:
        "pointer",

    boxShadow:
        "0 5px 14px rgba(0,59,122,0.18)",

    whiteSpace:
        "nowrap",
};


const emptyBox = {

    padding:
        "50px",

    textAlign:
        "center",

    color:
        "#94a3b8",

    background:
        "#ffffff",

    border:
        "1px solid #e2e8f0",

    borderRadius:
        "14px",
};


export default ExpensePage;